import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'

import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { useGroupsListQuery } from '~/queries/user-groups'
import { useAddGroupsToUserMutation, keys as mwmUsersKeys } from '~/queries/user-management'
import { reactSelectMenuPositionDef } from '~/utils/options'

const MODAL_ID = 'add-groups-to-user'

export interface FormValues {
  groups: IOption[]
}

const validationSchema = Yup.object({
  groups: Yup.array()
    .of(Yup.object().shape({ label: Yup.string().required(), value: Yup.string().required() }))
    .min(1, FORM_ERRORS.AT_LEAST_ONE)
    .required(FORM_ERRORS.REQUIRED),
})

interface FormProps {
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
  userGroups: string[]
  userId: string
}

const AddGroupsForm = ({ onClose, onFailure, onSuccess, userGroups, userId }: FormProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      groups: [],
    },
    resolver: yupResolver(validationSchema),
  })

  /** Queries & Mutations */

  const { mutateAsync: addGroupsMutation } = useAddGroupsToUserMutation()

  const { data: groups } = useGroupsListQuery()

  const groupsOptions =
    groups?.data
      .filter(({ id }) => !userGroups.includes(id))
      .map(({ group_name, id }) => ({
        label: group_name,
        value: id,
      })) || []

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    const { groups } = values

    if (groups.length) {
      try {
        const groupIds = groups.map(({ value }) => value)
        await addGroupsMutation({ groupIds, userId })
        await queryClient.invalidateQueries({ queryKey: mwmUsersKeys.details(userId) })
        onSuccess()
      } catch (e) {
        captureException(e)
        onFailure()
      }
    }
  }

  const handleGroupChange = (value: IOption) => setValue('groups', [value])

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <Controller
          control={control}
          name="groups"
          render={({ field }) => (
            <SelectInput
              {...field}
              {...reactSelectMenuPositionDef(MODAL_ID)}
              autoFocus
              error={errors.groups?.message}
              label="Groups"
              onChange={(value) => handleGroupChange(value as IOption)}
              options={groupsOptions}
              placeholder="Select groups"
              required
            />
          )}
        />
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          Save
        </Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const AddGroupsModal = ({ isOpen, onClose, onFailure, onSuccess, userGroups, userId }: ModalProps): ReactElement => {
  return (
    <Modal id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Add groups to user</ModalHeader>
      <AddGroupsForm
        onClose={onClose}
        onFailure={onFailure}
        onSuccess={onSuccess}
        userGroups={userGroups}
        userId={userId}
      />
    </Modal>
  )
}

export default AddGroupsModal
